import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import FaqSection from "@/components/FaqSection";
import Seo from "@/components/Seo";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-4 py-3 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50 transition-all";
const LABEL = "text-sm font-medium text-charcoal-600 mb-2 block";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "general", destination: "", move_from: "", move_to: "", timeline: "", message: "", preferred_language: "en" });

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("about") === "drivers") {
      setForm((f) => ({ ...f, message: "Question about drivers: " }));
    }
  }, []);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/brand").then((res) => setBrand(res.data)).catch(() => {});
  }, []);
  const [brand, setBrand] = useState({ contact_email: "info@holamovers.com", support_email: "info@holamovers.com" });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact Us — Questions About Moving to Mexico"
        description="Have a question about relocating to Mérida or elsewhere in Mexico? Send the Hola Movers team a message — we read every one and we're happy to help."
        path="/contact"
      />
      <section className="mx-auto max-w-3xl px-6 lg:px-12 pt-16 lg:pt-24 pb-12">
        <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">Contact</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
          Tell us about your move.
        </h1>
        <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">
          No pressure, no payment — just send us a message and we'll get back to you. Or, if you'd like a
          dedicated 45-minute session, <Link to="/book" className="text-terracotta underline underline-offset-2">book an intro call</Link>.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-12 pb-16">
        {sent ? (
          <div data-testid="contact-success" className="rounded-3xl bg-sage-light p-10 text-center">
            <CheckCircle2 className="mx-auto text-sage" size={48} strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-2xl text-charcoal-800">Message received — thank you!</h2>
            <p className="mt-3 text-charcoal-600">
              We've got your note and will be in touch soon. Take a deep breath — you're in good hands.
            </p>
            <Link to="/" className="mt-6 inline-flex rounded-full bg-terracotta px-7 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              Back home
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white p-8 lg:p-10 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] space-y-6">
            <div>
              <label className={LABEL}>What is this about? *</label>
              <select
                data-testid="contact-inquiry-type"
                required
                className={FIELD}
                value={form.inquiry_type}
                onChange={set("inquiry_type")}
              >
                <option value="general">General inquiry</option>
                <option value="quote">Quote / moving question</option>
                <option value="support">Problem / support issue</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL}>Which destination are you considering?</label>
              <select
                data-testid="contact-destination"
                className={FIELD}
                value={form.destination}
                onChange={set("destination")}
              >
                <option value="">Select one…</option>
                <option value="Mérida">I'm planning for Mérida</option>
                <option value="Mérida or another city">Considering Mérida or another city</option>
                <option value="Another location">Another location in Mexico</option>
                <option value="Not sure yet">Not sure yet — need guidance</option>
              </select>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={LABEL}>Your name *</label>
                <input data-testid="contact-name" required className={FIELD} value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className={LABEL}>Email *</label>
                <input data-testid="contact-email" type="email" required className={FIELD} value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={LABEL}>Phone</label>
                <input data-testid="contact-phone" className={FIELD} value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className={LABEL}>Hoped-for timeline</label>
                <input data-testid="contact-timeline" placeholder="e.g. within 6 months" className={FIELD} value={form.timeline} onChange={set("timeline")} />
              </div>
              <div>
                <label className={LABEL}>Moving from</label>
                <input data-testid="contact-from" placeholder="City, State" className={FIELD} value={form.move_from} onChange={set("move_from")} />
              </div>
              <div>
                <label className={LABEL}>Moving to</label>
                <input data-testid="contact-to" placeholder="Anywhere in Mexico?" className={FIELD} value={form.move_to} onChange={set("move_to")} />
              </div>
            </div>
            <div>
              <label className={LABEL}>Tell us about your move *</label>
              <textarea data-testid="contact-message" required rows={5} className={FIELD} value={form.message} onChange={set("message")} />
            </div>
            <div>
              <label className={LABEL}>Email me in</label>
              <select data-testid="contact-language" className={FIELD} value={form.preferred_language} onChange={set("preferred_language")}>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            {error && <p data-testid="contact-error" className="text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-12 pb-16">
        <div data-testid="contact-direct-emails" className="rounded-3xl bg-sand-50 border border-sand-200 p-8 lg:p-10">
          <h2 className="font-serif text-2xl text-charcoal-800">Prefer to reach us directly?</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-clay-700 font-medium mb-2">WhatsApp</p>
              <p className="text-sm text-charcoal-600 mb-2">Fastest way to reach us — chat with the team:</p>
              <a
                data-testid="contact-whatsapp"
                href="https://wa.me/529993616898?text=Hola!%20I%20have%20a%20question%20about%20moving%20to%20Mexico."
                target="_blank"
                rel="noopener noreferrer"
                className="text-terracotta font-medium underline underline-offset-2 hover:text-terracotta-hover"
              >
                +52 999 361 6898
              </a>
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-clay-700 font-medium mb-2">General &amp; quotes</p>
              <p className="text-sm text-charcoal-600 mb-2">For general questions, moving inquiries, or quotes:</p>
              <a
                data-testid="contact-email-info"
                href={`mailto:${brand.contact_email}?subject=HolaMovers%20Inquiry`}
                className="text-terracotta font-medium underline underline-offset-2 hover:text-terracotta-hover"
              >
                {brand.contact_email}
              </a>
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-clay-700 font-medium mb-2">Support</p>
              <p className="text-sm text-charcoal-600 mb-2">For problems or support with an existing matter:</p>
              <a
                data-testid="contact-email-support"
                href={`mailto:${brand.support_email}?subject=HolaMovers%20Support%20Request`}
                className="text-terracotta font-medium underline underline-offset-2 hover:text-terracotta-hover"
              >
                {brand.support_email}
              </a>
            </div>
          </div>
        </div>
      </section>

      <FaqSection page="contact" />
    </div>
  );
}
